import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Tooltip from '@material-ui/core/Tooltip';
import UnkownIcon from '@material-ui/icons/HelpOutline';
import QueueIcon from '@material-ui/icons/HourglassEmpty';
import FailedIcon from '@material-ui/icons/ErrorOutline';
import AbortedIcon from '@material-ui/icons/CancelPresentation';
import CompletedIcon from '@material-ui/icons/CheckCircleOutline';
import RejectedIcon from '@material-ui/icons/NotInterested';
import TimeoutIcon from '@material-ui/icons/Update';
import RunningIcon from '@material-ui/icons/Settings';
import {
  JOB_STATUS_UNKOWN,
  JOB_STATUS_QUEUED,
  JOB_STATUS_RUNNING,
  JOB_STATUS_ABORTED,
  JOB_STATUS_FAILED,
  JOB_STATUS_COMPLETED,
  JOB_STATUS_REJECTED,
  JOB_STATUS_DISCONNECTED,
  JOB_STATUS_IDLETIMEOUT,
} from '../../services/JobService';
import './JobStatus.scss';

//
// Icons for default job statuses
//
const AlwaysTrue = () => true;
const makeStatusIcon = (Icon, { className: staticClassName, style: staticStyle } = {}) => {
  const StatusIcon = ({ className, style }) => {
    return (
      <Icon
        className={classnames(className, staticClassName)}
        style={style || staticStyle ? { ...(staticStyle || {}), ...(style || {}) } : undefined}
      />
    );
  };

  StatusIcon.propTypes = {
    className: PropTypes.string,
    style: PropTypes.shape({}),
  };

  StatusIcon.defaultProps = {
    className: undefined,
    style: undefined,
  };

  return React.memo(StatusIcon, AlwaysTrue);
};

const MemoizedUnkownIcon = makeStatusIcon(UnkownIcon);
const MemoizedQueueIcon = makeStatusIcon(QueueIcon);
const MemoizedRunningIcon = makeStatusIcon(RunningIcon, { className: 'grb-rotate' });
const MemoizedAbortedIcon = makeStatusIcon(AbortedIcon);
const MemoizedFailedIcon = makeStatusIcon(FailedIcon);
const MemoizedCompletedIcon = makeStatusIcon(CompletedIcon);
const MemoizedRejectedIcon = makeStatusIcon(RejectedIcon);
const MemoizedDisconnectedIcon = makeStatusIcon(({ className }) => (
  <svg
    viewBox="0 0 24 24"
    className={classnames('grb-svg-icon', 'MuiSvgIcon-root', className)}
  >
    <path d="M4,1C2.89,1 2,1.89 2,3V7C2,8.11 2.89,9 4,9H1V11H13V9H10C11.11,9 12,8.11 12,7V3C12,1.89 11.11,1 10,1H4M4,3H10V7H4V3M14,13C12.89,13 12,13.89 12,15V19C12,20.11 12.89,21 14,21H11V23H23V21H20C21.11,21 22,20.11 22,19V15C22,13.89 21.11,13 20,13H14M3.88,13.46L2.46,14.88L4.59,17L2.46,19.12L3.88,20.54L6,18.41L8.12,20.54L9.54,19.12L7.41,17L9.54,14.88L8.12,13.46L6,15.59L3.88,13.46M14,15H20V19H14V15Z" />
  </svg>
));
const MemoizedTimeoutIcon = makeStatusIcon(TimeoutIcon);

//
// Renderers for default job statuses
//
const DefaultRenderers = {
  [JOB_STATUS_UNKOWN]: {
    className: 'grb-jobstatus status-unknown',
    tooltip: 'UNKOWN',
    icon: MemoizedUnkownIcon,
    filterText: 'UNKOWN',
  },
  [JOB_STATUS_QUEUED]: {
    className: 'grb-jobstatus status-queued',
    tooltip: 'QUEUED',
    icon: MemoizedQueueIcon,
    filterText: 'QUEUED',
  },
  [JOB_STATUS_RUNNING]: {
    className: 'grb-jobstatus status-running',
    tooltip: 'RUNNING',
    icon: MemoizedRunningIcon,
    filterText: 'RUNNING',
  },
  [JOB_STATUS_ABORTED]: {
    className: 'grb-jobstatus status-aborted',
    tooltip: 'ABORTED',
    icon: MemoizedAbortedIcon,
    filterText: 'ABORTED',
  },
  [JOB_STATUS_FAILED]: {
    className: 'grb-jobstatus status-failed',
    tooltip: 'FAILED',
    icon: MemoizedFailedIcon,
    filterText: 'FAILED',
  },
  [JOB_STATUS_COMPLETED]: {
    className: 'grb-jobstatus status-completed',
    tooltip: 'COMPLETED',
    icon: MemoizedCompletedIcon,
    filterText: 'COMPLETED',
  },
  [JOB_STATUS_REJECTED]: {
    className: 'grb-jobstatus status-rejected',
    tooltip: 'REJECTED',
    icon: MemoizedRejectedIcon,
    filterText: 'REJECTED',
  },
  [JOB_STATUS_DISCONNECTED]: {
    className: 'grb-jobstatus status-disconnected',
    tooltip: 'DISCONNECTED',
    icon: MemoizedDisconnectedIcon,
    filterText: 'DISCONNECTED',
  },
  [JOB_STATUS_IDLETIMEOUT]: {
    className: 'grb-jobstatus status-idletimeout',
    tooltip: 'IDLETIMEOUT',
    icon: MemoizedTimeoutIcon,
    filterText: 'IDLETIMEOUT',
  },
};

const getJobStatusStates = ({ jobService, getJobStatusRenderer = () => null }) => {
  const statuses = jobService.getJobStatuses();
  return statuses.reduce((acc, s) => {
    const customRenderer = getJobStatusRenderer(s.value) || {};
    const state = {
      value: s.value,
      ...(DefaultRenderers[s.name] || {}),
      ...(customRenderer),
    };
    state.renderTooltip = customRenderer.renderTooltip || ((job, content) => {
      const { errorMsg } = jobService.getError(job);
      return errorMsg
        ? (
          <Tooltip title={(
            <React.Fragment>
              {state.tooltip}<br />
              {errorMsg}
            </React.Fragment>
          )}
          >
            {content}
          </Tooltip>
        ) : (
          <Tooltip title={state.tooltip}>
            {content}
          </Tooltip>
        );
    });
    acc[s.value] = state;
    return acc;
  }, new Array(statuses.length));
};

const makeStatusComponent = (config) => {
  const {
    jobService,
    job: staticJob, // When the component is used in the Job dashboard page to display the icon for the job of the page
  } = config;
  const getJobStatus = jobService.getJobStatus.bind(jobService);
  const StatusStates = getJobStatusStates(config);

  const JobStatus = (props) => {
    const job = props.object || staticJob;
    const { value = getJobStatus(job) } = props;
    const State = StatusStates[value] || {};
    const { icon: Icon = TimeoutIcon, className, Component } = State;
    if (Component) {
      return (
        <div className={classnames('grb-jobstatus-ctnr', className)}>
          <Component job={job} />
        </div>
      );
    }
    return (
      <div className={classnames('grb-jobstatus-ctnr', className)}>
        {State.renderTooltip(job, (
          <div className={className}>
            <Icon className={props.className} style={props.style} />
          </div>
        ))}
      </div>
    );
  };

  JobStatus.propTypes = {
    value: PropTypes.number,
    object: PropTypes.shape({}),
    className: PropTypes.string,
    style: PropTypes.shape({}),
  };

  JobStatus.defaultProps = {
    value: undefined,
    object: undefined,
    className: undefined,
    style: undefined,
  };

  return JobStatus;
};

export { makeStatusComponent as default, getJobStatusStates };
